import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { usePaginationContext } from '../../contexts/PaginationContext';

const PaginationButton = ({ direction, onPageChange }) => {
	const buttonRef = useRef(null);
	const isNext = direction === 'next';
	const { offset, itemsPerPage, canGoNext, canGoPrevious } =
		usePaginationContext();
	const currentPage = Math.floor(offset / itemsPerPage) + 1;

	const isDisabled = isNext ? !canGoNext : !canGoPrevious;

	const targetPage = isNext ? currentPage + 1 : currentPage - 1;

	// Blur button when it becomes disabled while focused
	useEffect(() => {
		if (isDisabled && buttonRef.current === document.activeElement) {
			buttonRef.current.blur();
		}
	}, [isDisabled]);

	const handleClick = (e) => {
		e.preventDefault();
		if (!isDisabled && onPageChange) {
			onPageChange();
		}
	};

	const iconClass = isNext
		? 'ddc-icon ddc-icon-skinny-arrow-right'
		: 'ddc-icon ddc-icon-skinny-arrow-left';

	const label = isNext
		? `Go to next page (${targetPage})`
		: `Go to previous page (${targetPage})`;

	const title = isDisabled
		? `${isNext ? 'Next' : 'Previous'} page not available`
		: `View ${isNext ? 'next' : 'previous'} page of matching results`;

	const className = `mx-3 pagination-${isNext ? 'next' : 'previous'}${
		isDisabled ? ' disabled' : ''
	}`;

	return (
		<li className={className}>
			{/* To use existing css we are using anchor tag taking reference from kinetic style guide */}
			{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
			<a
				ref={buttonRef}
				aria-label={label}
				aria-disabled={isDisabled}
				onClick={handleClick}
				title={title}
				className={isDisabled ? 'disabled' : ''}
				href="#"
				tabIndex={isDisabled ? -1 : 0}
			>
				<i className={iconClass} aria-hidden="true" />
			</a>
		</li>
	);
};

PaginationButton.propTypes = {
	direction: PropTypes.oneOf(['next', 'previous']).isRequired,
	onPageChange: PropTypes.func.isRequired
};

export default PaginationButton;
